import { useState, useEffect, useMemo, useCallback } from "react";
import { fetchWrapper } from "src/helpers";
import Skeleton from "src/common/Skeleton";

interface SupplyDemandRow {
  date: string;
  supply: number;
  demand: number;
}

interface SupplyDemandProps {
  type?: "past" | "retest";
  derivativeType?: boolean;
}

const SupplyDemandTable = ({
  type = "past",
  derivativeType,
}: SupplyDemandProps) => {
  const defaultDays = type === "retest" ? 15 : 30;
  const maxDays = type === "retest" ? 15 : 30;

  const [selectedDays, setSelectedDays] = useState<number>(defaultDays);
  const [data, setData] = useState<SupplyDemandRow[]>([]);
  const [avgSupply, setAvgSupply] = useState<number>(0);
  const [avgDemand, setAvgDemand] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setSelectedDays(defaultDays);
  }, [defaultDays]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWrapper.get(
        `/api/dashboard/order-blocks/supply-demand?type=${type}&days=${selectedDays}&derivatives=${derivativeType}`,
      );
      if (response.success) {
        setData(response.data);
        setAvgSupply(response.avg_supply);
        setAvgDemand(response.avg_demand);
      }
    } catch (error) {
      console.error("Failed to fetch order block data", error);
    } finally {
      setLoading(false);
    }
  }, [type, selectedDays, derivativeType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const title = useMemo(
    () =>
      `${type === "retest" ? "Retest of Order Blocks in Past" : "New Order Blocks in Past"} ${selectedDays} Days`,
    [type, selectedDays],
  );

  const { globalMax } = useMemo(() => {
    if (data.length === 0) return { globalMax: 100 };
    const absoluteMax = Math.max(
      ...data.map((d) => d.supply),
      ...data.map((d) => d.demand),
    );
    return { globalMax: absoluteMax * 1.5 };
  }, [data]);

  const getBarWidth = useCallback(
    (value: number) => {
      if (value <= 0 || globalMax <= 0) return "0%";
      return `${(value / globalMax) * 100}%`;
    },
    [globalMax],
  );

  const handleDaysChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedDays(Number(e.target.value));
    },
    [],
  );

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center gap-2 px-2 pb-1 border-b border-[#d0d4e4] dark:border-[#394564]">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
          {title}
        </h3>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
            Day Range:
          </span>
          <input
            type="range"
            min="1"
            max={maxDays}
            value={selectedDays}
            onChange={handleDaysChange}
            className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded min-w-[40px] text-center">
            {selectedDays}d
          </span>
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[350px]">
        <table className="w-full text-xs">
          <thead className="bg-gray-100 dark:bg-[#30324e] border-b border-[#d0d4e4] dark:border-[#394564]">
            <tr>
              <th className="py-2 px-3 text-sm text-start font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree w-[30%]">
                Date
              </th>
              <th
                colSpan={2}
                className="py-2 px-3 text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree w-[70%]"
              >
                <div className="grid grid-cols-2 text-center">
                  <div>Supply</div>
                  <div>Demand</div>
                </div>
              </th>
            </tr>
          </thead>
        </table>
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-xs">
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                [...Array(10)].map((_, index) => (
                  <tr
                    key={index}
                    className="hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="py-2 px-3 w-[30%]">
                      <Skeleton className="w-16 h-4 rounded" />
                    </td>
                    <td colSpan={2} className="py-2 px-3 w-[70%]">
                      <div className="flex items-center gap-1.5">
                        <Skeleton className="w-8 h-4 rounded" />
                        <div className="flex-1 min-w-[80px]">
                          <Skeleton className="h-3 w-full rounded" />
                        </div>
                        <span className="text-gray-400 dark:text-gray-500 text-xs">
                          |
                        </span>
                        <div className="flex-1 min-w-[80px]">
                          <Skeleton className="h-3 w-full rounded" />
                        </div>
                        <Skeleton className="w-8 h-4 rounded" />
                      </div>
                    </td>
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="py-4 text-center text-gray-500 dark:text-gray-400"
                  >
                    <p className="text-xs">No data available</p>
                  </td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <tr
                    key={index}
                    className="hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="py-2 px-3 text-gray-700 dark:text-gray-300 font-medium text-xs w-[30%]">
                      {row.date}
                    </td>
                    <td colSpan={2} className="py-2 px-3 w-[70%]">
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-900 dark:text-white font-bold text-xs w-8">
                          {row.supply}
                        </span>
                        <div className="flex-1 h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden min-w-[80px] flex flex-row-reverse">
                          <div
                            className="h-full bg-gradient-to-l from-red-500 to-red-700 dark:from-red-400 dark:to-red-500 transition-all duration-300"
                            style={{ width: getBarWidth(row.supply) }}
                          />
                        </div>
                        <span className="text-gray-400 dark:text-gray-500 text-xs">
                          |
                        </span>
                        <div className="flex-1 h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden min-w-[80px] flex">
                          <div
                            className="h-full bg-gradient-to-r from-green-500 to-green-700 dark:from-green-400 dark:to-green-500 transition-all duration-300"
                            style={{ width: getBarWidth(row.demand) }}
                          />
                        </div>
                        <span className="text-gray-900 dark:text-white font-bold text-xs w-8 text-right">
                          {row.demand}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && data.length > 0 && (
          <div className="border-t-2 border-blue-200 dark:border-blue-800">
            <table className="w-full text-xs">
              <tfoot className="bg-gray-100 dark:bg-[#30324e]">
                <tr className="font-semibold">
                  <td className="py-2 px-3 text-blue-900 dark:text-blue-200 text-xs w-[30%]">
                    Average ({selectedDays}d)
                  </td>
                  <td colSpan={2} className="py-2 px-3 w-[70%]">
                    <div className="flex items-center gap-1.5">
                      <span className="text-blue-900 dark:text-blue-200 font-bold text-xs w-8">
                        {avgSupply.toLocaleString()}
                      </span>
                      <div className="flex-1 h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden min-w-[80px] flex flex-row-reverse">
                        <div
                          className="h-full bg-gradient-to-l from-red-500 to-red-700 dark:from-red-400 dark:to-red-500 transition-all duration-300"
                          style={{ width: getBarWidth(avgSupply) }}
                        />
                      </div>
                      <span className="text-blue-400 dark:text-blue-500 text-xs">
                        |
                      </span>
                      <div className="flex-1 h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden min-w-[80px] flex">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-green-700 dark:from-green-400 dark:to-green-500 transition-all duration-300"
                          style={{ width: getBarWidth(avgDemand) }}
                        />
                      </div>
                      <span className="text-blue-900 dark:text-blue-200 font-bold text-xs w-8 text-right">
                        {avgDemand.toLocaleString()}
                      </span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplyDemandTable;
